import outboundRingingAudioSrc from "../common/assets/audio/outbound-ringing.mp3";
import busyToneAudioSrc from "../common/assets/audio/busy-tone.mp3";

const BUSY_TONE_MAX_MS = 5000;

const isBusyCallEndReason = (callEndReason) => {
  const code = Number(callEndReason?.code);
  const message = String(callEndReason?.message || "").toLowerCase();
  return code === 486 || message.includes("busy");
};

export class OutboundCallToneController {
  constructor() {
    this.ringingAudio = null;
    this.busyAudio = null;
    this.currentRingingCallId = null;
    this.busyStopTimeoutId = null;
  }

  ensureAudio() {
    if (typeof window === "undefined") {
      return false;
    }
    if (typeof window.Audio !== "function") {
      return false;
    }
    const AudioCtor = window.Audio;
    if (!this.ringingAudio) {
      this.ringingAudio = new AudioCtor(outboundRingingAudioSrc);
      this.ringingAudio.loop = true;
      this.ringingAudio.preload = "auto";
      this.ringingAudio.volume = 1.0;
    }
    if (!this.busyAudio) {
      this.busyAudio = new AudioCtor(busyToneAudioSrc);
      this.busyAudio.loop = true;
      this.busyAudio.preload = "auto";
      this.busyAudio.volume = 1.0;
    }
    return true;
  }

  async safePlay(audioEl) {
    if (!audioEl) return;
    try {
      await audioEl.play();
    } catch (err) {
      console.warn("Audio playback blocked or failed", err);
    }
  }

  resetAudio(audioEl) {
    if (!audioEl) return;
    audioEl.pause();
    audioEl.currentTime = 0;
  }

  async startRinging(callId) {
    if (!callId) return;

    if (this.currentRingingCallId === callId && this.ringingAudio && !this.ringingAudio.paused) {
      return;
    }

    if (!this.ensureAudio()) return;
    this.stopBusyTone();
    this.stopRinging();

    this.currentRingingCallId = callId;
    this.ringingAudio.currentTime = 0;
    await this.safePlay(this.ringingAudio);
  }

  stopRinging(callId) {
    if (callId && this.currentRingingCallId && callId !== this.currentRingingCallId) {
      return;
    }

    this.resetAudio(this.ringingAudio);
    this.currentRingingCallId = null;
  }

  async playBusyTone() {
    if (!this.ensureAudio()) return;
    this.stopRinging();
    this.stopBusyTone();

    this.busyAudio.currentTime = 0;
    await this.safePlay(this.busyAudio);
    this.busyStopTimeoutId = setTimeout(() => {
      this.stopBusyTone();
    }, BUSY_TONE_MAX_MS);
  }

  stopBusyTone() {
    this.resetAudio(this.busyAudio);
    if (this.busyStopTimeoutId) {
      clearTimeout(this.busyStopTimeoutId);
      this.busyStopTimeoutId = null;
    }
  }

  stopAllTones() {
    this.stopRinging();
    this.stopBusyTone();
  }

  async handleOutboundStateChange({ callId, direction, callState, callEndReason }) {
    if (direction !== "Outbound") {
      return;
    }

    if (callState === "None" || callState === "Connecting" || callState === "Ringing") {
      await this.startRinging(callId);
      return;
    }

    if (callState === "EarlyMedia") {
      this.stopRinging(callId);
      return;
    }

    if (
      callState === "Connected" ||
      callState === "Disconnected" ||
      callState === "Disconnecting"
    ) {
      this.stopRinging(callId);
    }

    if (callState === "Busy") {
      await this.playBusyTone();
      return;
    }

    if (callState === "Disconnected") {
      if (isBusyCallEndReason(callEndReason)) {
        await this.playBusyTone();
      } else {
        this.stopBusyTone();
      }
    }
  }

  async dispose() {
    this.stopAllTones();
    this.ringingAudio = null;
    this.busyAudio = null;
  }
}
